/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { focusable, focusElement, ignoreFocus } from '../utils/focus.js';
import { listenForAttributeChange } from '../utils/events.js';
import { getFlattenedDOMTree } from '../utils/traversal.js';
/**
 * Provides a focus first behavior to any component via the cds-first-focus attribute
 */
export function firstFocus(config = { fallback: 'focusable' }) {
    return (target) => target.addInitializer((instance) => new FirstFocusController(instance, config));
}
export class FirstFocusController {
    constructor(host, config = { fallback: 'focusable' }) {
        this.host = host;
        this.config = config;
        this.host.addController(this);
    }
    async hostConnected() {
        await this.host.updateComplete;
        this.observer = listenForAttributeChange(this.host, 'hidden', () => this.cdsFocusFirst());
        this.cdsFocusFirst();
    }
    hostDisconnected() {
        this.observer?.disconnect();
    }
    cdsFocusFirst() {
        if (!ignoreFocus(this.host)) {
            const root = this.host.shadowRoot ? this.host.shadowRoot : this.host;
            const rootHost = root.querySelector('.private-host') ?? this.host;
            const elements = getFlattenedDOMTree(root).filter(i => !i.hasAttribute('cds-focus-boundary'));
            const firstFocus = elements.find(i => i.hasAttribute('cds-first-focus'));
            const focusableElement = this.config.fallback === 'focusable'
                ? elements.find(i => focusable(i) && !i.classList.contains('private-host'))
                : null;
            const host = this.config.fallback === 'none' ? null : rootHost;
            const focus = firstFocus ?? focusableElement ?? host;
            if (focus) {
                focusElement(focus);
            }
        }
    }
}
